import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePageTracking } from '../hooks/usePageTracking';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import GlobalSearch from './GlobalSearch';
import NotificationsPanel from './NotificationsPanel';
import QuickAddTask from './QuickAddTask';
import CreditCounter from './CreditCounter';
import api from '../api/client';
import {
  LayoutDashboard,
  FolderKanban,
  CheckSquare,
  BarChart3,
  TrendingUp,
  Lightbulb,
  Settings,
  LogOut,
  Rocket,
  Brain,
  PieChart,
  Calendar,
  CalendarRange,
  Bot,
  Menu,
  X,
  Sun,
  Moon,
  Columns3,
  Building2,
  Crown,
  Lock,
  Clock,
  ShieldAlert,
  GitBranch,
  History,
  Zap,
  Briefcase,
  Users,
  UsersRound,
  FileText,
  Target,
  Network,
  Activity,
  FlaskConical,
  Globe,
  Plug,
  KeyRound,
  MessageSquare,
  Sparkles,
  BookOpen,
  Layers,
  ChevronRight,
  ChevronLeft,
  Star,
  Crosshair,
  Compass,
  Gauge,
  DollarSign,
  Link2,
  HelpCircle,
  Workflow,
  ChevronDown,
  Upload,
  Cpu,
} from 'lucide-react';

// Feature-to-plan mapping for nav gating
const NAV_FEATURE_GATES = {
  '/executive': 'executive_dashboard',
  '/reports': 'reports',
  '/forecasts': 'reports',
  '/ai-analysis': 'ai',
  '/ai-pm': 'ai',
  '/ai-engineering': 'ai_engineering',
  '/sprints': 'sprints',
  '/time-tracking': 'time_tracking',
  '/time': 'time_tracking',
  '/risk': 'ai',
  '/gantt': 'reports',
};

// Plan feature sets (aligned with billing: free / starter / growth / enterprise)
const PLAN_FEATURES = {
  free: new Set(['basic_tasks', 'basic_projects', 'kanban']),
  starter: new Set(['basic_tasks', 'basic_projects', 'kanban', 'ai', 'time_tracking', 'sprints', 'reports', 'email_notifications', 'api_access']),
  growth: new Set(['basic_tasks', 'basic_projects', 'kanban', 'ai', 'audit_trail', 'time_tracking', 'sprints', 'reports', 'executive_dashboard', 'email_notifications', 'api_access', 'ai_engineering']),
  pro: new Set(['basic_tasks', 'basic_projects', 'kanban', 'ai', 'audit_trail', 'time_tracking', 'sprints', 'reports', 'executive_dashboard', 'email_notifications', 'api_access', 'ai_engineering']),
  enterprise: new Set(['basic_tasks', 'basic_projects', 'kanban', 'ai', 'audit_trail', 'time_tracking', 'sprints', 'reports', 'executive_dashboard', 'email_notifications', 'sso', 'custom_fields', 'api_access', 'api_full', 'ai_engineering']),
};

const CLIENT_NAV_ITEMS = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['client'] },
  { name: 'Projects', href: '/projects', icon: FolderKanban, roles: ['client'] },
  { name: 'My Tasks', href: '/my-tasks', icon: CheckSquare, roles: ['client'] },
  { name: 'Command Center', href: '/my-agents', icon: Bot, roles: ['client'] },
];

const NAV_SECTIONS = [
  {
    label: 'Core',
    items: [
      { name: 'Live Activity', href: '/live-activity', icon: Activity, roles: ['admin', 'manager'] },
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'developer'] },
      { name: 'My Tasks', href: '/my-tasks', icon: CheckSquare, roles: ['admin', 'manager', 'developer'] },
      { name: 'Command Center', href: '/my-agents', icon: Bot, roles: ['admin', 'manager', 'developer'] },
      { name: 'What are we upto?', href: '/progress', icon: Rocket, roles: ['admin', 'manager', 'developer'] },
      { name: 'AI Manager', href: '/ai-pm', icon: Bot, roles: ['admin', 'manager'] },
    ],
  },
  {
    label: 'Portfolio',
    items: [
      { name: 'Overview', href: '/portfolio', icon: Briefcase, roles: ['admin', 'manager'] },
      { name: 'Goals', href: '/okrs', icon: Target, roles: ['admin', 'manager'] },
      { name: 'Monday Cadence', href: '/portfolio/weekly-brief', icon: Sparkles, roles: ['admin', 'manager'] },
      { name: 'Goal Categories', href: '/portfolio/goal-categories', icon: Layers, roles: ['admin', 'manager'] },
      { name: 'Programs', href: '/portfolio/programs', icon: FolderKanban, roles: ['admin', 'manager'] },
      { name: 'Teams', href: '/portfolio/teams', icon: Users, roles: ['admin', 'manager'] },
      { name: 'Roadmap', href: '/portfolio/roadmap', icon: Calendar, roles: ['admin', 'manager'] },
      { name: 'Risks', href: '/portfolio/risks', icon: ShieldAlert, roles: ['admin', 'manager'] },
      { name: 'Reports', href: '/portfolio/reports', icon: FileText, roles: ['admin', 'manager'] },
      { name: 'Import', href: '/portfolio/import', icon: Upload, roles: ['admin', 'manager'] },
    ],
  },
  {
    label: 'Legacy exec',
    items: [
      { name: 'Org metrics', href: '/cto-dashboard', icon: Gauge, roles: ['admin'] },
      { name: 'Executive', href: '/executive', icon: PieChart, roles: ['admin', 'manager'] },
      { name: 'Persona View', href: '/persona-dashboard', icon: Crown, roles: ['admin', 'manager'] },
    ],
  },
  {
    label: 'Plan & Align',
    items: [
      { name: 'Projects', href: '/projects', icon: FolderKanban, roles: ['admin', 'manager', 'developer', 'member'] },
      { name: 'Clients', href: '/clients', icon: Building2, roles: ['admin', 'manager'] },
      { name: 'Resources', href: '/resource-planning', icon: Users, roles: ['admin', 'manager'] },
      { name: 'Team', href: '/team', icon: UsersRound, roles: ['admin', 'manager', 'developer'] },
    ],
  },
  {
    label: 'Execute',
    items: [
      { name: 'Kanban', href: '/kanban', icon: Columns3, roles: ['admin', 'manager', 'developer'] },
      { name: 'Calendar', href: '/calendar', icon: Calendar, roles: ['admin', 'manager', 'developer'] },
      { name: 'Sprints', href: '/sprints', icon: CalendarRange, roles: ['admin', 'manager', 'developer'] },
      { name: 'Epics', href: '/tasks', icon: CheckSquare, roles: ['admin', 'manager', 'developer'] },
      { name: 'Gantt Chart', href: '/gantt', icon: GitBranch, roles: ['admin', 'manager'] },
      { name: 'Time Tracking', href: '/time', icon: Clock, roles: ['admin', 'manager', 'developer'] },
      { name: 'AI Skills', href: '/ai-skills', icon: Bot, roles: ['admin', 'manager', 'developer'] },
      { name: 'AI Automations', href: '/ai-automations', icon: Workflow, roles: ['admin', 'manager', 'developer'] },
      { name: 'AI Engineering', href: '/ai-engineering', icon: Cpu, roles: ['admin', 'owner', 'manager', 'developer'] },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { name: 'Analytics', href: '/analytics', icon: Activity, roles: ['admin', 'manager'] },
      { name: 'Outcomes', href: '/outcomes', icon: Crosshair, roles: ['admin', 'manager'] },
      { name: 'AI Insights', href: '/ai-insights', icon: Sparkles, roles: ['admin', 'manager'] },
      { name: 'Work Graph', href: '/work-graph', icon: Network, roles: ['admin', 'manager'] },
      { name: 'Collaboration', href: '/collab', icon: BookOpen, roles: ['admin', 'manager', 'member'] },
      { name: 'AI Analysis', href: '/ai-analysis', icon: Brain, roles: ['admin', 'manager'] },
      { name: 'Risk Prediction', href: '/risk', icon: ShieldAlert, roles: ['admin', 'manager'] },
      { name: 'Forecasts', href: '/forecasts', icon: TrendingUp, roles: ['admin', 'manager'] },
      { name: 'Reports', href: '/reports', icon: BarChart3, roles: ['admin', 'manager'] },
    ],
  },
  {
    label: 'Finance',
    items: [
      { name: 'Overview', href: '/finance', icon: DollarSign, roles: ['admin'] },
      { name: 'Budget Burn', href: '/budget-burn', icon: TrendingUp, roles: ['admin'] },
      { name: 'Invoices', href: '/invoices', icon: FileText, roles: ['admin'] },
    ],
  },
  {
    label: 'Knowledge',
    items: [
      { name: 'Docs', href: '/docs', icon: FileText, roles: ['admin', 'manager', 'developer'] },
      { name: 'Templates', href: '/templates', icon: FileText, roles: ['admin', 'manager'] },
      { name: 'Ideas', href: '/ideas', icon: Lightbulb, roles: ['admin', 'manager', 'developer'] },
      { name: 'Help Center', href: '/help', icon: HelpCircle, roles: ['admin', 'manager', 'developer'] },
      { name: 'Quick Start Guide', href: '/quick-start', icon: Rocket, roles: ['admin', 'manager', 'developer'] },
    ],
  },
  {
    label: 'System',
    items: [
      { name: 'Automation', href: '/automation', icon: Zap, roles: ['admin', 'manager'] },
      { name: 'Guided Workflows', href: '/guided-workflows', icon: Compass, roles: ['admin', 'manager', 'developer'] },
      { name: 'Workflow Builder', href: '/workflow-builder', icon: Layers, roles: ['admin', 'manager'] },
      { name: 'Scenarios', href: '/scenarios', icon: FlaskConical, roles: ['admin', 'manager'] },
      { name: 'Portals', href: '/stakeholder-portals', icon: Globe, roles: ['admin', 'manager'] },
      { name: 'Integrations', href: '/integrations', icon: Plug, roles: ['admin', 'manager'] },
      { name: 'Slack Shortcuts', href: '/slack-shortcuts', icon: MessageSquare, roles: ['admin', 'manager'] },
      { name: 'Slack Mappings', href: '/slack-user-mappings', icon: Link2, roles: ['admin'] },
      { name: 'Audit Log', href: '/audit', icon: History, roles: ['admin'] },
      { name: 'Org Settings', href: '/org-settings', icon: Building2, roles: ['admin'] },
      { name: 'Billing', href: '/billing', icon: Crown, roles: ['admin'] },
      { name: 'Admin', href: '/admin', icon: Settings, roles: ['admin'] },
    ],
  },
];

const SECTION_ICONS = {
  'Core': LayoutDashboard,
  'Plan & Align': Target,
  'Execute': CheckSquare,
  'Intelligence': Sparkles,
  'Finance': DollarSign,
  'Knowledge': BookOpen,
  'System': Settings,
};

const Layout = () => {
  const { user, org, logout, isClientUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  usePageTracking();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navScrollRef = useRef(null);
  const navScrollPos = useRef(0);
  
  const mainScrollRef = useRef(null);
  const [topBarHidden, setTopBarHidden] = useState(false);
  const topBarHiddenRef = useRef(false);
  const lastScrollTop = useRef(0);
  const hideStartScrollTop = useRef(0);

  const { theme, toggleTheme } = useTheme();
  // AskFlo now navigates to /my-agents?tab=chat
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('visionflo-sidebar-collapsed');
    if (saved === null) return false;
    return saved === 'true';
  });

  const [collapsedSections, setCollapsedSections] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const saved = localStorage.getItem('visionflo-nav-collapsed-sections');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const toggleSection = (label) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [label]: !prev[label] };
      localStorage.setItem('visionflo-nav-collapsed-sections', JSON.stringify(next));
      return next;
    });
  };
  
  const isDarkMode = theme === 'dark';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('visionflo-sidebar-collapsed', sidebarCollapsed ? 'true' : 'false');
  }, [sidebarCollapsed]);

  // Close sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
    if (topBarHiddenRef.current) {
      topBarHiddenRef.current = false;
      setTopBarHidden(false);
    }
    lastScrollTop.current = 0;
    hideStartScrollTop.current = 0;
    // Restore desktop nav scroll position after route change
    requestAnimationFrame(() => {
      if (navScrollRef.current) {
        navScrollRef.current.scrollTop = navScrollPos.current;
      }
    });
  }, [location.pathname]);

  // Prevent body scroll when mobile menu open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    const scrollEl = mainScrollRef.current;
    if (!scrollEl) return;
    let ticking = false;

    const setHidden = (value) => {
      if (topBarHiddenRef.current === value) return;
      topBarHiddenRef.current = value;
      setTopBarHidden(value);
    };

    const handleScroll = () => {
      const current = scrollEl.scrollTop;
      const prev = lastScrollTop.current;
      const delta = current - prev;
      const buffer = 6;

      if (Math.abs(delta) < buffer) {
        lastScrollTop.current = current;
        return;
      }

      if (delta > 0) {
        if (!topBarHiddenRef.current && current > 48) {
          hideStartScrollTop.current = current;
          setHidden(true);
        }
      } else {
        if (topBarHiddenRef.current) {
          const revealOffset = scrollEl.clientHeight * 0.5;
          if (hideStartScrollTop.current - current >= revealOffset || current < 24) {
            setHidden(false);
          }
        } else if (current < 24) {
          setHidden(false);
        }
      }

      lastScrollTop.current = current;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        handleScroll();
        ticking = false;
      });
    };

    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', onScroll);
  }, []);

  const visibleSections = useMemo(
    () => {
      if (isClientUser) {
        return [{ label: 'Client Portal', items: CLIENT_NAV_ITEMS }];
      }
      return NAV_SECTIONS
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => item.roles.includes(user?.role)),
        }))
        .filter((section) => section.items.length > 0);
    },
    [user?.role, isClientUser]
  );

  const [hoveredSection, setHoveredSection] = useState(null);
  const hoverOpenTimeout = useRef(null);
  const hoverCloseTimeout = useRef(null);

  const PINNED_KEY = 'visionflo-nav-pinned';
  const RECENT_KEY = 'visionflo-nav-recent';
  const MAX_PINNED = 8;
  const MAX_RECENT = 6;

  const [pinnedItems, setPinnedItems] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = JSON.parse(localStorage.getItem(PINNED_KEY) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch (err) {
      return [];
    }
  });

  const [recentItems, setRecentItems] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch (err) {
      return [];
    }
  });

  // Load pinned/recent from server on mount
  const prefsLoaded = useRef(false);
  useEffect(() => {
    if (prefsLoaded.current) return;
    prefsLoaded.current = true;
    api.get('/api/dashboard/ui-prefs').then(res => {
      const d = res.data;
      if (d.pinned?.length) { setPinnedItems(d.pinned); localStorage.setItem(PINNED_KEY, JSON.stringify(d.pinned)); }
      if (d.recent?.length) { setRecentItems(d.recent); localStorage.setItem(RECENT_KEY, JSON.stringify(d.recent)); }
    }).catch(() => {});
  }, []);

  // Debounced sync to server
  const syncTimer = useRef(null);
  const syncPrefs = useCallback((pinned, recent) => {
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      api.put('/api/dashboard/ui-prefs', { pinned, recent }).catch(() => {});
    }, 1000);
  }, []);


  const openHoverSection = (label) => {
    if (hoverCloseTimeout.current) {
      clearTimeout(hoverCloseTimeout.current);
      hoverCloseTimeout.current = null;
    }
    if (hoverOpenTimeout.current) {
      clearTimeout(hoverOpenTimeout.current);
      hoverOpenTimeout.current = null;
    }
    setHoveredSection(label);
  };

  const scheduleHoverOpen = (label) => {
    if (hoverOpenTimeout.current) {
      clearTimeout(hoverOpenTimeout.current);
    }
    hoverOpenTimeout.current = setTimeout(() => {
      openHoverSection(label);
    }, 160);
  };

  const scheduleHoverClose = () => {
    if (hoverCloseTimeout.current) {
      clearTimeout(hoverCloseTimeout.current);
    }
    if (hoverOpenTimeout.current) {
      clearTimeout(hoverOpenTimeout.current);
      hoverOpenTimeout.current = null;
    }
    hoverCloseTimeout.current = setTimeout(() => {
      setHoveredSection(null);
    }, 180);
  };

  const clearHoverTimers = () => {
    if (hoverOpenTimeout.current) {
      clearTimeout(hoverOpenTimeout.current);
      hoverOpenTimeout.current = null;
    }
    if (hoverCloseTimeout.current) {
      clearTimeout(hoverCloseTimeout.current);
      hoverCloseTimeout.current = null;
    }
  };

  const navItemMap = useMemo(() => {
    const map = new Map();
    visibleSections.forEach((section) => {
      section.items.forEach((item) => {
        map.set(item.href, item);
      });
    });
    return map;
  }, [visibleSections]);

  const resolvedPinned = useMemo(
    () => pinnedItems.map((href) => navItemMap.get(href)).filter(Boolean),
    [pinnedItems, navItemMap]
  );

  const resolvedRecent = useMemo(() => {
    const recent = recentItems.filter((href) => !pinnedItems.includes(href));
    return recent.map((href) => navItemMap.get(href)).filter(Boolean);
  }, [recentItems, pinnedItems, navItemMap]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(PINNED_KEY, JSON.stringify(pinnedItems));
    syncPrefs(pinnedItems, recentItems);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedItems]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(RECENT_KEY, JSON.stringify(recentItems));
    syncPrefs(pinnedItems, recentItems);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentItems]);

  useEffect(() => {
    const item = navItemMap.get(location.pathname);
    if (!item) return;
    setRecentItems((prev) => {
      if (prev[0] === item.href) return prev;
      const next = [item.href, ...prev.filter((href) => href !== item.href)];
      return next.slice(0, MAX_RECENT);
    });
  }, [location.pathname, navItemMap]);

  useEffect(() => {
    clearHoverTimers();
    setHoveredSection(null);
  }, [location.pathname]);

  const togglePin = (href) => {
    setPinnedItems((prev) => {
      if (prev.includes(href)) {
        return prev.filter((itemHref) => itemHref !== href);
      }
      return [href, ...prev].slice(0, MAX_PINNED);
    });
  };

  // Determine which features are locked based on org plan
  const currentPlan = org?.plan || 'free';
  const allowedFeatures = PLAN_FEATURES[currentPlan] || PLAN_FEATURES.free;
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(null);

  const isFeatureLocked = (href) => {
    const requiredFeature = NAV_FEATURE_GATES[href];
    if (!requiredFeature) return false;
    return !allowedFeatures.has(requiredFeature);
  };

  const handleLockedClick = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setShowUpgradePrompt(item.name);
    setTimeout(() => setShowUpgradePrompt(null), 3000);
  };

  const renderNavItem = (item, options = {}) => {
    const { pinAlwaysVisible = false, onNavigate, compact = false, forceExpanded = false } = options;
    const Icon = item.icon;
    const locked = isFeatureLocked(item.href);
    const isPinned = pinnedItems.includes(item.href);
    const isCompact = compact && !forceExpanded;
    const pinVisibility = pinAlwaysVisible ? 'opacity-100' : 'opacity-0 group-hover:opacity-100';

    const pinButton = isCompact ? null : (
      <button
        type="button"
        className={`transition-opacity ${pinVisibility}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePin(item.href);
        }}
        aria-label={isPinned ? `Unpin ${item.name}` : `Pin ${item.name}`}
        title={isPinned ? 'Unpin' : 'Pin'}
        data-testid={`sidebar-pin-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Star className={`w-3.5 h-3.5 ${isPinned ? 'text-[#FDAB3D] fill-[#FDAB3D]' : 'text-[#C3C6D4]'}`} />
      </button>
    );

    const rightIcons = isCompact ? null : (
      <div className="ml-auto flex items-center gap-2">
        {locked && <Lock className="w-3 h-3 text-[#FDAB3D]" />}
        {pinButton}
      </div>
    );

    if (locked) {
      return (
        <div
          key={item.name}
          className={`monday-nav-item opacity-50 cursor-not-allowed relative group ${isCompact ? 'justify-center compact' : ''}`}
          onClick={(e) => handleLockedClick(e, item)}
          title={item.name}
          data-testid={`sidebar-nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}-locked`}
        >
          <Icon className="w-4.5 h-4.5" />
          {isCompact ? <span className="sr-only">{item.name}</span> : <span className="flex-1">{item.name}</span>}
          {rightIcons}
          {showUpgradePrompt === item.name && (
            <div className="absolute left-full ml-2 top-0 z-50 bg-white dark:bg-[#323338] border border-[#E6E9EF] dark:border-[#3a3b40] rounded-lg shadow-lg px-3 py-2 w-48 pointer-events-none" data-testid="upgrade-tooltip">
              <p className="text-xs font-medium text-[#323338] dark:text-white">Upgrade Required</p>
              <p className="text-[10px] text-[#676879] dark:text-[#9CA0AF] mt-0.5">This feature requires a Pro or Enterprise plan.</p>
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        key={item.name}
        to={item.href}
        end={item.href === '/portfolio'}
        className={({ isActive }) =>
          `monday-nav-item group ${isCompact ? 'justify-center compact' : ''} ${isActive ? 'active' : ''}`
        }
        onClick={() => {
          if (onNavigate) onNavigate();
        }}
        title={item.name}
        data-testid={`sidebar-nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className="w-4.5 h-4.5" />
        {isCompact ? <span className="sr-only">{item.name}</span> : <span>{item.name}</span>}
        {rightIcons}
      </NavLink>
    );
  };

  const renderSidebarPanel = (isMobile = false) => {
    const isCompact = !isMobile && sidebarCollapsed;
    return (
      <>
      {isMobile && (
        <div className="flex items-center justify-end h-14 px-4 border-b border-[#E6E9EF] dark:border-[#3a3b40] flex-shrink-0">
          <button
            className="w-8 h-8 flex items-center justify-center rounded-lg text-[#676879] dark:text-[#9CA0AF] hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30]"
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-close-btn"
            aria-label="Close navigation menu"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
      )}

      {resolvedPinned.length > 0 && (
        <div className={`${isCompact ? 'px-2' : 'px-3'} pt-2`}>
          {!isCompact && (
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA0AF] dark:text-[#666]">Pinned</p>
          )}
          <div className={`space-y-0.5 ${isCompact ? '' : 'mt-1'}`}>
            {resolvedPinned.map((item) => renderNavItem(item, { pinAlwaysVisible: true, compact: isCompact, ...(isMobile && { onNavigate: () => setMobileOpen(false) }) }))}
          </div>
        </div>
      )}

      <nav
        className={`flex-1 ${isCompact ? 'px-2' : 'px-3'} py-2 overflow-y-auto`}
        aria-label="Sidebar navigation"
        ref={isMobile ? undefined : navScrollRef}
        onScroll={isMobile ? undefined : (e) => { navScrollPos.current = e.target.scrollTop; }}
      >
        <Popover
          open={hoveredSection === 'recent'}
          onOpenChange={(open) => {
            if (open) {
              openHoverSection('recent');
            } else if (isMobile) {
              setHoveredSection(null);
            }
          }}
        >
          <PopoverTrigger asChild>
            <button
              onMouseEnter={() => { if (!isMobile) scheduleHoverOpen('recent'); }}
              onMouseLeave={() => { if (!isMobile) scheduleHoverClose(); }}
              onFocus={() => { if (!isMobile) openHoverSection('recent'); }}
              onBlur={() => { if (!isMobile) scheduleHoverClose(); }}
              onClick={(e) => {
                if (!isMobile) {
                  e.preventDefault();
                  return;
                }
                if (hoveredSection === 'recent') {
                  setHoveredSection(null);
                } else {
                  openHoverSection('recent');
                }
              }}
              className={`monday-nav-item w-full ${isCompact ? 'justify-center compact' : 'justify-between'}`}
              aria-label="Recent items"
              title="Recent"
              data-testid="sidebar-main-recent"
            >
              <span className={`flex items-center ${isCompact ? '' : 'gap-3'}`}>
                <History className="w-4.5 h-4.5" />
                {isCompact ? <span className="sr-only">Recent</span> : 'Recent'}
              </span>
              {!isCompact && <ChevronRight className="w-4 h-4 text-[#9CA0AF]" />}
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            sideOffset={12}
            className="p-3 w-64"
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            onMouseEnter={() => openHoverSection('recent')}
            onMouseLeave={scheduleHoverClose}
          >
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#9CA0AF] mb-2">
              Recent
            </div>
            {resolvedRecent.length === 0 ? (
              <div className="text-xs text-[#676879]">No recent items yet.</div>
            ) : (
              <div className="space-y-0.5">
                {resolvedRecent.map((item) =>
                  renderNavItem(item, {
                    onNavigate: () => {
                      clearHoverTimers();
                      setHoveredSection(null);
                    },
                    forceExpanded: true,
                  })
                )}
              </div>
            )}
          </PopoverContent>
        </Popover>

        <div className="my-3 border-t border-[#E6E9EF] dark:border-[#3a3b40]" style={{ marginTop: '0.35rem', marginBottom: '0.75rem' }} />

        {visibleSections.map((section) => {
          const Icon = SECTION_ICONS[section.label] || FolderKanban;
          const isOpen = hoveredSection === section.label;
          const isActive = section.items.some((item) => item.href === location.pathname);

          if (!isCompact) {
            const isSectionCollapsed = !!collapsedSections[section.label];
            return (
              <div key={section.label} className="pb-2">
                <button
                  onClick={() => toggleSection(section.label)}
                  className="px-1 pb-1.5 pt-0.5 w-full flex items-center justify-between group cursor-pointer"
                  data-testid={`nav-section-toggle-${section.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA0AF] dark:text-[#666] group-hover:text-foreground transition-colors">
                    {section.label}
                  </p>
                  <ChevronDown className={`w-3 h-3 text-[#9CA0AF] dark:text-[#666] transition-transform duration-200 ${isSectionCollapsed ? '-rotate-90' : ''}`} />
                </button>
                {!isSectionCollapsed && (
                  <div className="space-y-0.5">
                    {section.items.map((item) => renderNavItem(item, isMobile ? { onNavigate: () => setMobileOpen(false) } : {}))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <Popover
              key={section.label}
              open={isOpen}
              onOpenChange={(open) => {
                if (open) {
                  openHoverSection(section.label);
                } else if (isMobile) {
                  setHoveredSection(null);
                }
              }}
            >
              <PopoverTrigger asChild>
                <button
                  onMouseEnter={() => scheduleHoverOpen(section.label)}
                  onMouseLeave={scheduleHoverClose}
                  onFocus={() => openHoverSection(section.label)}
                  onBlur={scheduleHoverClose}
                  onClick={(e) => {
                    if (!isMobile) {
                      e.preventDefault();
                      return;
                    }
                    if (hoveredSection === section.label) {
                      setHoveredSection(null);
                    } else {
                      openHoverSection(section.label);
                    }
                  }}
                  className={`monday-nav-item w-full justify-center compact ${isActive ? 'active' : ''}`}
                  aria-label={`${section.label} menu`}
                  title={section.label}
                  data-testid={`sidebar-main-${section.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="flex items-center">
                    <Icon className="w-4.5 h-4.5" />
                    <span className="sr-only">{section.label}</span>
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="right"
                align="start"
                sideOffset={12}
                className="p-3 w-64"
                onOpenAutoFocus={(event) => event.preventDefault()}
                onCloseAutoFocus={(event) => event.preventDefault()}
                onMouseEnter={() => openHoverSection(section.label)}
                onMouseLeave={scheduleHoverClose}
              >
                <div className="text-[11px] font-semibold uppercase tracking-wider text-[#9CA0AF] mb-2">
                  {section.label}
                </div>
                <div className="space-y-0.5">
                  {section.items.map((item) =>
                    renderNavItem(item, {
                      onNavigate: () => {
                        clearHoverTimers();
                        setHoveredSection(null);
                      },
                      forceExpanded: true,
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>
          );
        })}
      </nav>

    </>
    );
  };

  return (
    <div className="flex h-screen bg-[#F6F7FB] dark:bg-[#1a1b1e]">
      {/* Skip to main content — WCAG AA */}
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>

      {/* Top Bar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 h-14 bg-white dark:bg-[#1e1f23] border-b border-[#E6E9EF] dark:border-[#3a3b40] transition-transform duration-200 ${topBarHidden ? '-translate-y-full pointer-events-none' : 'translate-y-0'}`}
      >
        <div className="h-full px-4 flex items-center gap-3">
          <div className="flex items-center gap-2 flex-shrink-0 md:flex-shrink md:min-w-0 md:overflow-hidden">
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-[#323338] dark:text-white hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30] -ml-2 flex-shrink-0"
              onClick={() => setMobileOpen(true)}
              data-testid="sidebar-hamburger-btn"
              aria-label="Open navigation menu"
              style={{ transition: 'background-color 0.15s ease' }}
            >
              <Menu className="w-5 h-5" aria-hidden="true" />
            </button>
            <button
              className="hidden md:flex w-9 h-9 items-center justify-center rounded-lg text-[#676879] dark:text-[#9CA0AF] hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30] -ml-1"
              onClick={() => setSidebarCollapsed((prev) => !prev)}
              data-testid="sidebar-collapse-btn"
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              style={{ transition: 'background-color 0.15s ease' }}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-4.5 h-4.5" aria-hidden="true" />
              ) : (
                <ChevronLeft className="w-4.5 h-4.5" aria-hidden="true" />
              )}
            </button>
            {isClientUser && user?.client_logo ? (
              <img
                src={user.client_logo}
                alt={user?.client_company_name ? `${user.client_company_name} logo` : 'Client logo'}
                className="w-7 h-7 rounded-md object-cover bg-white"
              />
            ) : isClientUser ? (
              <div className="w-7 h-7 rounded-md bg-emerald-500/10 dark:bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-3.5 h-3.5 text-emerald-600" />
              </div>
            ) : org?.logo_url ? (
              <img
                src={org.logo_url}
                alt={org?.name ? `${org.name} logo` : 'Organization logo'}
                className="w-7 h-7 rounded-md object-cover bg-white"
              />
            ) : (
              <div className="w-7 h-7 rounded-md bg-[#0073EA]/10 dark:bg-[#0073EA]/20 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-3.5 h-3.5 text-[#0073EA]" />
              </div>
            )}
            <h1 className="text-base font-semibold text-[#323338] dark:text-white truncate hidden sm:block" style={{ fontFamily: 'var(--font-heading)' }}>
              {isClientUser ? (user?.client_company_name || 'Client Portal') : (org?.name || 'Organization')}
            </h1>
          </div>

          <div className="hidden md:flex flex-1 justify-center min-w-0">
            <div className="flex items-center gap-2 w-[520px] lg:w-[640px] xl:w-[720px]">
              <GlobalSearch triggerClassName="flex-1" triggerWidthClass="max-w-none" />
              <QuickAddTask variant="inline" label="Quick Create" />
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Mobile: compact search + quick create icons */}
            <div className="flex md:hidden items-center gap-0.5">
              <GlobalSearch triggerClassName="" triggerWidthClass="w-9" mobileIcon />
              <QuickAddTask variant="icon" />
            </div>
            {!isClientUser && (
            <button
              type="button"
              onClick={() => {
                if (location.pathname === '/my-agents') {
                  // Already on Command Center — trigger new session
                  window.dispatchEvent(new CustomEvent('flo-new-chat'));
                } else {
                  navigate('/my-agents?tab=chat');
                }
              }}
              className="flex items-center gap-2 px-2 sm:px-3 h-9 rounded-full border border-border bg-muted text-foreground hover:bg-muted/80 transition-colors"
              data-testid="ask-flo-button"
            >
              <span
                className="w-5 h-5 rounded-lg flex items-center justify-center"
                style={{ background: 'conic-gradient(from 210deg, #00C875, #0073EA, #A25DDC, #FDAB3D, #00C875)' }}
              >
                <Sparkles className="w-3 h-3 text-white" />
              </span>
              <span className="text-sm font-medium hidden sm:inline">Ask Flo</span>
            </button>
            )}
            <CreditCounter />
            <NotificationsPanel />
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center w-9 h-9 rounded-lg text-[#676879] dark:text-[#9CA0AF] hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30]"
              data-testid="dark-mode-toggle"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <Sun className="w-4.5 h-4.5" aria-hidden="true" /> : <Moon className="w-4.5 h-4.5" aria-hidden="true" />}
            </button>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0 overflow-hidden ring-2 ring-transparent hover:ring-[#0073EA]/40 transition-all"
                  style={{ backgroundColor: user?.avatar_url ? 'transparent' : '#0073EA' }}
                  aria-label={user?.name ? `Signed in as ${user.name}` : 'Signed in user'}
                  data-testid="user-avatar-btn"
                >
                  {user?.avatar_url ? (
                    <img src={user.avatar_url.startsWith('http') ? user.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    user?.name?.charAt(0).toUpperCase()
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-52 p-1" align="end">
                <button
                  onClick={() => navigate(`/team/${user?.id}`)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground rounded-md hover:bg-muted transition-colors"
                  data-testid="my-profile-link"
                >
                  <Users className="w-4 h-4" />
                  My Profile
                </button>
                <button
                  type="button"
                  onClick={() => {
                    navigate('/account');
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground rounded-md hover:bg-muted transition-colors"
                  data-testid="account-settings-link"
                >
                  <KeyRound className="w-4 h-4" />
                  Connect via MCP
                </button>
                <button
                  onClick={logout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 rounded-md hover:bg-muted transition-colors"
                  data-testid="logout-btn-dropdown"
                >
                  <LogOut className="w-4 h-4" />
                  Log out
                </button>
              </PopoverContent>
            </Popover>
            <button
              type="button"
              onClick={logout}
              className="w-9 h-9 rounded-lg flex items-center justify-center text-[#323338] dark:text-[#9CA0AF] border border-[#C3C6D4] dark:border-[#3a3b40] hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30]"
              data-testid="logout-button"
              aria-label="Log out of VisionFlo"
              title="Logout"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="sidebar-mobile-overlay">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            style={{ backdropFilter: 'blur(4px)' }}
          />
          <aside
            className="absolute left-0 top-14 bottom-0 w-72 max-w-[85vw] bg-white dark:bg-[#1e1f23] flex flex-col shadow-xl"
            style={{ animation: 'slideIn 0.2s ease-out' }}
            role="navigation"
            aria-label="Main navigation"
          >
            {renderSidebarPanel(true)}
          </aside>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex md:flex-col ${sidebarCollapsed ? 'md:w-16' : 'md:w-64'} bg-white dark:bg-[#1e1f23] border-r border-[#E6E9EF] dark:border-[#3a3b40] fixed ${topBarHidden ? 'top-0' : 'top-14'} bottom-0 z-40 transition-all duration-200`}
        role="navigation"
        aria-label="Main navigation"
      >
        {topBarHidden && (
          <div className="flex items-center gap-2 h-14 px-3 border-b border-[#E6E9EF] dark:border-[#3a3b40] flex-shrink-0">
            <button
              className="w-9 h-9 flex items-center justify-center rounded-lg text-[#676879] dark:text-[#9CA0AF] hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30] -ml-1"
              onClick={() => setSidebarCollapsed((prev) => !prev)}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              style={{ transition: 'background-color 0.15s ease' }}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-4.5 h-4.5" aria-hidden="true" />
              ) : (
                <ChevronLeft className="w-4.5 h-4.5" aria-hidden="true" />
              )}
            </button>
            {org?.logo_url ? (
              <img src={org.logo_url} alt={org?.name || 'Logo'} className="w-7 h-7 rounded-md object-cover bg-white" />
            ) : (
              <div className="w-7 h-7 rounded-md bg-[#0073EA]/10 dark:bg-[#0073EA]/20 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-3.5 h-3.5 text-[#0073EA]" />
              </div>
            )}
            {!sidebarCollapsed && (
              <span className="text-base font-semibold text-[#323338] dark:text-white truncate" style={{ fontFamily: 'var(--font-heading)' }}>
                {org?.name || 'Organization'}
              </span>
            )}
          </div>
        )}
        {renderSidebarPanel(false)}
      </aside>

      {/* Main Content */}
      <main
        id="main-content"
        ref={mainScrollRef}
        className={`flex-1 ml-0 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'} overflow-y-auto pt-14 transition-all duration-200`}
        role="main"
        aria-label="Page content"
        tabIndex="-1"
      >
        <Outlet />
      </main>

    </div>
  );
};

export default Layout;
